import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import type {
  ProjectTemplate,
  TemplateTagGroup,
} from '@claude-code-api/shared';
import Autoplay from 'embla-carousel-autoplay';
import { BarChart3, Search, SlidersHorizontal, Star, X } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '@/components/ui/carousel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useKeyboardShortcutEngine,
  type ShortcutConfig,
} from '@/hooks/useKeyboardShortcuts';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  buildTemplateCatalogApiPath,
  getCompactTemplateBadgeClassName,
  getTemplateIconClassName,
  getTemplateIconComponent,
  getTemplateIconStyleAttribute,
  getTemplatePrimaryImage,
  getTemplateScreenshotUrls,
  getTemplateTagColorClassName,
  getTemplateTagDefinitions,
} from '@/lib/template-catalog';

type MarketplaceResponse = {
  data?: ProjectTemplate[];
  filters?: {
    tagGroups?: Array<
      TemplateTagGroup & { tagDefinitions?: ProjectTemplate['tagDefinitions'] }
    >;
  };
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
};

type MarketplaceTag = NonNullable<ProjectTemplate['tagDefinitions']>[number];

type FilterTag = MarketplaceTag & {
  filterGroupId: string;
  groupName: string;
};

type FullscreenPanState = {
  x: number;
  y: number;
};

function sortMarketplaceTags(tags: MarketplaceTag[]): MarketplaceTag[] {
  return [...tags].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
  );
}

function buildMarketplaceRequestUrl(
  locale: string,
  options?: {
    page?: number;
    limit?: number;
    search?: string;
    tagIds?: string[];
    featured?: boolean;
  },
) {
  const url = new URL(
    buildTemplateCatalogApiPath('/api/templates', locale),
    window.location.origin,
  );

  if (options?.page) url.searchParams.set('page', String(options.page));
  if (options?.limit) url.searchParams.set('limit', String(options.limit));
  if (options?.featured) url.searchParams.set('featured', '1');

  const normalizedSearch = String(options?.search || '').trim();
  if (normalizedSearch) {
    url.searchParams.set('search', normalizedSearch);
  }

  if (options?.tagIds?.length) {
    url.searchParams.set('tagIds', options.tagIds.join(','));
  }

  return `${url.pathname}${url.search}`;
}

function TemplatePreviewMedia({
  template,
  className = '',
  enableCarousel = true,
}: {
  template: ProjectTemplate;
  className?: string;
  enableCarousel?: boolean;
}) {
  const TemplateIcon = getTemplateIconComponent(template);
  const primaryImage = getTemplatePrimaryImage(template);
  const screenshots = getTemplateScreenshotUrls(template);
  const slides =
    screenshots.length > 0 ? screenshots : primaryImage ? [primaryImage] : [];
  const [previewApi, setPreviewApi] = useState<CarouselApi>();
  const [previewIndex, setPreviewIndex] = useState(0);

  useEffect(() => {
    if (!previewApi) return;

    const onSelect = () => {
      setPreviewIndex(previewApi.selectedScrollSnap());
    };

    onSelect();
    previewApi.on('select', onSelect);
    previewApi.on('reInit', onSelect);

    return () => {
      previewApi.off('select', onSelect);
      previewApi.off('reInit', onSelect);
    };
  }, [previewApi]);

  const shouldUseCarousel = enableCarousel && slides.length > 1;

  if (slides.length > 0) {
    return (
      <div className={`relative h-full w-full overflow-hidden ${className}`}>
        {shouldUseCarousel ? (
          <Carousel
            setApi={setPreviewApi}
            opts={{ loop: true, duration: 12 }}
            className="h-full w-full"
          >
            <CarouselContent className="ml-0 h-full">
              {slides.map((slide, index) => (
                <CarouselItem
                  key={`${template.id}-preview-${index}`}
                  className="h-full pl-0"
                >
                  <div className="h-full w-full">
                    <img
                      src={slide}
                      alt={`${template.name} preview ${index + 1}`}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-3 z-10 h-8 w-8 border-white/20 bg-slate-950/55 text-white hover:bg-slate-950/75 hover:text-white" />
            <CarouselNext className="right-3 z-10 h-8 w-8 border-white/20 bg-slate-950/55 text-white hover:bg-slate-950/75 hover:text-white" />
            <div className="absolute inset-x-0 bottom-3 z-10 flex justify-center gap-1.5">
              {slides.map((slide, index) => (
                <button
                  key={`${slide}-preview-dot-${index}`}
                  type="button"
                  aria-label={`${template.name} preview ${index + 1}`}
                  onClick={() => previewApi?.scrollTo(index)}
                  className={`h-2 rounded-full transition ${index === previewIndex ? 'w-6 bg-white' : 'w-2 bg-white/45 hover:bg-white/70'}`}
                />
              ))}
            </div>
          </Carousel>
        ) : (
          <img
            src={slides[0]}
            alt={template.name}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        )}
        <div className="absolute inset-0 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08),inset_0_-90px_120px_rgba(15,23,42,0.16),inset_0_40px_80px_rgba(59,130,246,0.10)]" />
      </div>
    );
  }

  return (
    <div
      className={`flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 ${className}`}
    >
      <div className="flex flex-col items-center gap-3 text-center text-white">
        <div
          className={`flex h-16 w-16 items-center justify-center rounded-2xl ${getTemplateIconClassName(template)}`}
          style={getTemplateIconStyleAttribute(template)}
        >
          <TemplateIcon className="h-8 w-8" />
        </div>
        <div>
          <div className="text-sm font-semibold">{template.name}</div>
          <div className="text-xs text-white/70">{template.description}</div>
        </div>
      </div>
    </div>
  );
}

export default function TemplateMarketplacePage() {
  const { t, i18n } = useTranslation(['templateMarketplace', 'common']);
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedTagsByGroup, setSelectedTagsByGroup] = useState<
    Record<string, string[]>
  >({});
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<ProjectTemplate | null>(null);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [fullscreenZoom, setFullscreenZoom] = useState(1);
  const [fullscreenPan, setFullscreenPan] = useState<FullscreenPanState>({
    x: 0,
    y: 0,
  });
  const [showFullscreenHint, setShowFullscreenHint] = useState(true);
  const [featuredApi, setFeaturedApi] = useState<CarouselApi>();
  const [detailApi, setDetailApi] = useState<CarouselApi>();
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [detailScreenshotIndex, setDetailScreenshotIndex] = useState(0);
  const fullscreenWheelAtRef = useRef(0);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const fullscreenViewerRef = useRef<HTMLDivElement | null>(null);
  const fullscreenDragRef = useRef<{
    active: boolean;
    originX: number;
    originY: number;
    startX: number;
    startY: number;
  }>({
    active: false,
    originX: 0,
    originY: 0,
    startX: 0,
    startY: 0,
  });

  const featuredAutoplay = useMemo(
    () =>
      Autoplay({
        delay: 5000,
        stopOnInteraction: true,
        stopOnMouseEnter: true,
      }),
    [],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 250);

    return () => window.clearTimeout(timer);
  }, [search]);

  const locale = i18n.resolvedLanguage || i18n.language;
  const selectedTagIds = useMemo(
    () =>
      Object.values(selectedTagsByGroup)
        .flatMap((values) => values)
        .sort(),
    [selectedTagsByGroup],
  );
  const selectedTagIdsKey = selectedTagIds.join(',');

  const templatesQuery = useInfiniteQuery({
    queryKey: [
      'template-marketplace',
      locale,
      debouncedSearch,
      selectedTagIdsKey,
    ],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const res = await fetch(
        buildMarketplaceRequestUrl(locale, {
          page: Number(pageParam),
          limit: 12,
          search: debouncedSearch,
          tagIds: selectedTagIds,
        }),
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error('load_failed');
      return (await res.json()) as MarketplaceResponse;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.pagination?.hasMore) return undefined;
      return lastPage.pagination.page + 1;
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const featuredTemplatesQuery = useQuery({
    queryKey: [
      'template-marketplace-featured',
      locale,
      debouncedSearch,
      selectedTagIdsKey,
    ],
    queryFn: async () => {
      const res = await fetch(
        buildMarketplaceRequestUrl(locale, {
          page: 1,
          limit: 8,
          search: debouncedSearch,
          tagIds: selectedTagIds,
          featured: true,
        }),
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error('load_failed');
      return (await res.json()) as MarketplaceResponse;
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!templatesQuery.error && !featuredTemplatesQuery.error) return;
    console.error(
      'Failed to load template marketplace:',
      templatesQuery.error || featuredTemplatesQuery.error,
    );
    toast.error(t('templateMarketplace:errors.load'));
  }, [featuredTemplatesQuery.error, t, templatesQuery.error]);

  const tagGroups = useMemo(
    () =>
      templatesQuery.data?.pages.find(
        (page) => (page.filters?.tagGroups || []).length,
      )?.filters?.tagGroups || [],
    [templatesQuery.data],
  );

  const templates = useMemo(
    () => templatesQuery.data?.pages.flatMap((page) => page.data || []) || [],
    [templatesQuery.data],
  );

  const featuredTemplates = useMemo(
    () => featuredTemplatesQuery.data?.data || [],
    [featuredTemplatesQuery.data],
  );

  const loading = templatesQuery.isPending && templates.length === 0;
  const pagination =
    templatesQuery.data?.pages[templatesQuery.data.pages.length - 1]
      ?.pagination;

  const managedTagIds = useMemo(
    () =>
      new Set(
        tagGroups.flatMap((group) =>
          (group.tagDefinitions || []).map((tag) => tag.id),
        ),
      ),
    [tagGroups],
  );

  const orderedTagGroups = useMemo(
    () =>
      [...tagGroups].sort(
        (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
      ),
    [tagGroups],
  );

  const categoryGroup =
    orderedTagGroups.find((group) => group.id === 'category') || null;
  const secondaryGroups = orderedTagGroups.filter(
    (group) => group.id !== 'category',
  );

  const activeFilterCount = useMemo(
    () =>
      Object.values(selectedTagsByGroup).reduce(
        (count, values) => count + values.length,
        0,
      ),
    [selectedTagsByGroup],
  );

  const selectedTagDefinitions = useMemo<FilterTag[]>(
    () =>
      orderedTagGroups.flatMap((group) =>
        sortMarketplaceTags(group.tagDefinitions || [])
          .filter((tag) =>
            (selectedTagsByGroup[group.id] || []).includes(tag.id),
          )
          .map((tag) => ({
            ...tag,
            filterGroupId: group.id,
            groupName: group.name,
          })),
      ),
    [orderedTagGroups, selectedTagsByGroup],
  );

  useEffect(() => {
    if (!featuredApi) return;

    const onSelect = () => {
      setFeaturedIndex(featuredApi.selectedScrollSnap());
    };

    onSelect();
    featuredApi.on('select', onSelect);
    featuredApi.on('reInit', onSelect);

    return () => {
      featuredApi.off('select', onSelect);
      featuredApi.off('reInit', onSelect);
    };
  }, [featuredApi]);

  useEffect(() => {
    if (!featuredApi) return;
    if (!featuredTemplates.length) {
      setFeaturedIndex(0);
      return;
    }

    featuredApi.reInit();
    featuredApi.scrollTo(0, true);
    setFeaturedIndex(0);
  }, [
    debouncedSearch,
    featuredApi,
    featuredTemplates.length,
    selectedTagIdsKey,
  ]);

  useEffect(() => {
    const element = loadMoreRef.current;
    if (
      !element ||
      !templatesQuery.hasNextPage ||
      templatesQuery.isFetchingNextPage
    )
      return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        void templatesQuery.fetchNextPage();
      },
      { rootMargin: '640px 0px' },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [
    templatesQuery.fetchNextPage,
    templatesQuery.hasNextPage,
    templatesQuery.isFetchingNextPage,
    templates.length,
  ]);

  useEffect(() => {
    if (!detailApi) return;

    const onSelect = () => {
      setDetailScreenshotIndex(detailApi.selectedScrollSnap());
    };

    onSelect();
    detailApi.on('select', onSelect);
    detailApi.on('reInit', onSelect);

    return () => {
      detailApi.off('select', onSelect);
      detailApi.off('reInit', onSelect);
    };
  }, [detailApi]);

  const detailScreenshots = selectedTemplate
    ? getTemplateScreenshotUrls(selectedTemplate)
    : [];
  const detailPrimaryImage = selectedTemplate
    ? getTemplatePrimaryImage(selectedTemplate)
    : null;
  const detailSlides = selectedTemplate
    ? detailScreenshots.length > 0
      ? detailScreenshots
      : detailPrimaryImage
        ? [detailPrimaryImage]
        : []
    : [];
  const fullscreenSlideIndex = fullscreenImage
    ? detailSlides.indexOf(fullscreenImage)
    : -1;

  useEffect(() => {
    if (!detailApi) return;
    detailApi.reInit();
    detailApi.scrollTo(0, true);
    setDetailScreenshotIndex(0);
  }, [detailApi, selectedTemplate?.id, detailSlides.length]);

  useEffect(() => {
    setFullscreenZoom(1);
    setFullscreenPan({ x: 0, y: 0 });
    setShowFullscreenHint(true);
  }, [fullscreenImage]);

  useEffect(() => {
    if (!fullscreenImage || !showFullscreenHint) return;

    const timer = window.setTimeout(() => {
      setShowFullscreenHint(false);
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [fullscreenImage, showFullscreenHint]);

  useEffect(() => {
    if (!fullscreenImage) return;
    fullscreenViewerRef.current?.focus();
  }, [fullscreenImage]);

  const clampFullscreenZoom = (value: number) =>
    Math.min(3, Math.max(0.6, Number(value.toFixed(2))));

  const stepFullscreenSlide = (direction: 1 | -1) => {
    if (!detailSlides.length) return;

    const currentIndex =
      fullscreenSlideIndex >= 0 ? fullscreenSlideIndex : detailScreenshotIndex;
    const nextIndex =
      (currentIndex + direction + detailSlides.length) % detailSlides.length;
    detailApi?.scrollTo(nextIndex);
    setDetailScreenshotIndex(nextIndex);
    setFullscreenImage(detailSlides[nextIndex] || null);
  };

  const hideFullscreenHint = () => {
    setShowFullscreenHint(false);
  };

  const fullscreenShortcuts = useMemo<ShortcutConfig[]>(
    () => [
      {
        key: 'arrowright',
        action: () => stepFullscreenSlide(1),
        description: 'Next screenshot',
        category: 'Template Preview',
      },
      {
        key: 'arrowdown',
        action: () => stepFullscreenSlide(1),
        description: 'Next screenshot',
        category: 'Template Preview',
      },
      {
        key: 'pagedown',
        action: () => stepFullscreenSlide(1),
        description: 'Next screenshot',
        category: 'Template Preview',
      },
      {
        key: 'arrowleft',
        action: () => stepFullscreenSlide(-1),
        description: 'Previous screenshot',
        category: 'Template Preview',
      },
      {
        key: 'arrowup',
        action: () => stepFullscreenSlide(-1),
        description: 'Previous screenshot',
        category: 'Template Preview',
      },
      {
        key: 'pageup',
        action: () => stepFullscreenSlide(-1),
        description: 'Previous screenshot',
        category: 'Template Preview',
      },
      {
        key: '+',
        action: () =>
          setFullscreenZoom((current) => clampFullscreenZoom(current + 0.12)),
        description: 'Zoom in',
        category: 'Template Preview',
      },
      {
        key: '=',
        action: () =>
          setFullscreenZoom((current) => clampFullscreenZoom(current + 0.12)),
        description: 'Zoom in',
        category: 'Template Preview',
      },
      {
        key: '-',
        action: () =>
          setFullscreenZoom((current) => clampFullscreenZoom(current - 0.12)),
        description: 'Zoom out',
        category: 'Template Preview',
      },
      {
        key: '0',
        action: () => setFullscreenZoom(1),
        description: 'Reset zoom',
        category: 'Template Preview',
      },
    ],
    [detailSlides.length, fullscreenSlideIndex],
  );

  useKeyboardShortcutEngine(fullscreenShortcuts, {
    enabled: Boolean(fullscreenImage),
    target: fullscreenViewerRef,
  });

  const openCreateFlow = (templateId: string) => {
    navigate(
      `/projects?openCreate=1&template=${encodeURIComponent(templateId)}`,
    );
  };

  const toggleTag = (groupId: string, tagId: string) => {
    setSelectedTagsByGroup((current) => {
      const nextGroupValues = current[groupId]?.includes(tagId)
        ? current[groupId].filter((value) => value !== tagId)
        : [...(current[groupId] || []), tagId];
      return { ...current, [groupId]: nextGroupValues };
    });
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedTagsByGroup({});
  };

  const renderTemplateTags = (template: ProjectTemplate, limit = 3) => {
    const tagDefinitions = sortMarketplaceTags(
      getTemplateTagDefinitions(template).filter(
        (tag) => managedTagIds.has(tag.id) && tag.groupId !== 'stack',
      ),
    );
    const visibleTags = tagDefinitions.slice(0, limit);
    const hiddenTagCount = Math.max(
      0,
      tagDefinitions.length - visibleTags.length,
    );

    return (
      <div className="flex flex-wrap gap-2">
        {visibleTags.map((tag) => (
          <Badge
            key={`${template.id}-${tag.id}`}
            variant="outline"
            className={getCompactTemplateBadgeClassName(
              getTemplateTagColorClassName(tag.colorToken),
            )}
          >
            {tag.name}
          </Badge>
        ))}
        {hiddenTagCount > 0 ? (
          <div className="group/hidden-tags relative">
            <Badge
              variant="outline"
              className={getCompactTemplateBadgeClassName(
                'cursor-default border-dashed bg-muted/60 text-muted-foreground',
              )}
            >
              +{hiddenTagCount}
            </Badge>
            <div className="pointer-events-none absolute bottom-full left-0 z-20 mb-2 w-72 rounded-xl border bg-popover p-3 text-popover-foreground opacity-0 shadow-lg transition-opacity duration-150 group-hover/hidden-tags:opacity-100">
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('templateMarketplace:tagsPopoverTitle')}
              </div>
              <div className="flex flex-wrap gap-2">
                {tagDefinitions.map((tag) => (
                  <Badge
                    key={`${template.id}-popover-${tag.id}`}
                    variant="outline"
                    className={getCompactTemplateBadgeClassName(
                      getTemplateTagColorClassName(tag.colorToken),
                    )}
                  >
                    {tag.name}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderTagGroup = (
    group: TemplateTagGroup & {
      tagDefinitions?: ProjectTemplate['tagDefinitions'];
    },
    options?: { maxVisible?: number; showOverflowTrigger?: boolean },
  ) => {
    const tags = sortMarketplaceTags(group.tagDefinitions || []);
    if (!tags.length) return null;
    const maxVisible = options?.maxVisible;
    const visibleTags =
      typeof maxVisible === 'number' ? tags.slice(0, maxVisible) : tags;
    const hiddenCount = Math.max(0, tags.length - visibleTags.length);

    return (
      <div key={group.id} className="space-y-3">
        <div>
          <div className="text-sm font-semibold">{group.name}</div>
          {group.description ? (
            <div className="text-xs text-muted-foreground">
              {group.description}
            </div>
          ) : null}
        </div>
        <div className="space-y-1.5">
          {visibleTags.map((tag) => {
            const isActive = (selectedTagsByGroup[group.id] || []).includes(
              tag.id,
            );
            return (
              <button
                key={tag.id}
                type="button"
                onClick={() => toggleTag(group.id, tag.id)}
                className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm transition ${isActive ? 'border-primary bg-primary/5 text-primary' : 'border-border/70 bg-background hover:border-border hover:bg-muted/40'}`}
              >
                <span>{tag.name}</span>
                {isActive ? (
                  <span className="text-xs">
                    {t('templateMarketplace:filters.active')}
                  </span>
                ) : null}
              </button>
            );
          })}
          {options?.showOverflowTrigger && hiddenCount > 0 ? (
            <button
              type="button"
              onClick={() => setFilterDialogOpen(true)}
              className="flex w-full items-center justify-between rounded-xl border border-dashed border-border/70 bg-background px-3 py-2 text-left text-sm text-muted-foreground transition hover:border-border hover:bg-muted/40 hover:text-foreground"
            >
              <span>
                {t('templateMarketplace:actions.checkMore', {
                  defaultValue: 'Check more',
                })}
              </span>
              <span className="text-xs">+{hiddenCount}</span>
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  const renderTemplateCard = (template: ProjectTemplate) => (
    <Card
      key={template.id}
      className="group overflow-hidden border-border/70 bg-card/95 transition hover:-translate-y-0.5 hover:shadow-lg"
    >
      <div className="relative aspect-[16/9] overflow-hidden border-b bg-slate-950">
        <TemplatePreviewMedia
          template={template}
          className="transition duration-300 group-hover:scale-[1.02]"
        />
        {template.featured ? (
          <div className="absolute left-4 top-4">
            <Badge
              variant="outline"
              className={getCompactTemplateBadgeClassName(
                'border-amber-200 bg-amber-50 text-amber-700 shadow-sm dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300',
              )}
            >
              <Star className="mr-1 h-3 w-3 fill-current" />
              {t('templateMarketplace:featured')}
            </Badge>
          </div>
        ) : null}
      </div>
      <CardContent className="space-y-4 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold leading-tight">
              {template.name}
            </h3>
            <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
              {template.description}
            </p>
          </div>
          <div className="shrink-0 rounded-lg border bg-muted/50 px-2.5 py-1 text-xs font-medium text-muted-foreground">
            {t('templateMarketplace:usageCount', {
              count: template.usageCount || 0,
            })}
          </div>
        </div>

        {renderTemplateTags(template)}

        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedTemplate(template)}
          >
            {t('templateMarketplace:actions.preview', {
              defaultValue: 'Check detail',
            })}
          </Button>
          <Button onClick={() => openCreateFlow(template.id)}>
            {t('templateMarketplace:actions.useTemplate')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const renderDetailContent = (template: ProjectTemplate) => {
    const coverageTags = sortMarketplaceTags(
      getTemplateTagDefinitions(template).filter(
        (tag) => managedTagIds.has(tag.id) && tag.groupId === 'stack',
      ),
    );
    const screenshotRefs = detailSlides;
    const detailBlocks = String(template.detailContent || '')
      .split(/\n{2,}/)
      .map((value) => value.trim())
      .filter(Boolean);

    return (
      <section className="border-t bg-muted/20 px-6 py-5">
        <div className="grid gap-6 lg:grid-cols-[1.25fr_0.75fr]">
          <div className="space-y-3 text-center lg:text-left">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground lg:text-left">
              {t('templateMarketplace:details.contentTitle', {
                defaultValue: 'Template notes',
              })}
            </div>
            <div className="space-y-3 text-sm leading-7 text-muted-foreground">
              {detailBlocks.length > 0 ? (
                detailBlocks.map((block, index) => {
                  const lines = block
                    .split('\n')
                    .map((line) => line.trim())
                    .filter(Boolean);
                  const titleLine = lines[0]?.endsWith(':')
                    ? lines[0].slice(0, -1)
                    : null;
                  const bulletLines = titleLine ? lines.slice(1) : lines;
                  const isBulletBlock =
                    bulletLines.length > 0 &&
                    bulletLines.every((line) => line.startsWith('- '));

                  if (titleLine && isBulletBlock) {
                    return (
                      <div
                        key={`${template.id}-detail-block-${index}`}
                        className="space-y-2"
                      >
                        <div className="text-sm font-semibold text-foreground">
                          {titleLine}
                        </div>
                        <ul className="space-y-1.5">
                          {bulletLines.map((line, bulletIndex) => (
                            <li
                              key={`${template.id}-detail-bullet-${index}-${bulletIndex}`}
                              className="flex items-start gap-2"
                            >
                              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/70" />
                              <span>{line.slice(2)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  }

                  if (isBulletBlock) {
                    return (
                      <ul
                        key={`${template.id}-detail-block-${index}`}
                        className="space-y-1.5"
                      >
                        {bulletLines.map((line, bulletIndex) => (
                          <li
                            key={`${template.id}-detail-bullet-${index}-${bulletIndex}`}
                            className="flex items-start gap-2"
                          >
                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/70" />
                            <span>{line.slice(2)}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  }

                  return (
                    <p key={`${template.id}-detail-paragraph-${index}`}>
                      {block}
                    </p>
                  );
                })
              ) : (
                <p>{template.description}</p>
              )}
            </div>
            {coverageTags.length > 0 ? (
              <div className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground lg:text-left">
                  {t('templateMarketplace:details.coverageTitle', {
                    defaultValue: 'Coverage',
                  })}
                </div>
                <div className="flex flex-wrap justify-center gap-2 lg:justify-start">
                  {coverageTags.map((tag) => (
                    <Badge
                      key={`${template.id}-content-${tag.id}`}
                      variant="outline"
                      className={getCompactTemplateBadgeClassName(
                        getTemplateTagColorClassName(tag.colorToken),
                      )}
                    >
                      {tag.name}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="space-y-3">
            {screenshotRefs.length > 0 ? (
              <div className="space-y-2">
                {screenshotRefs.map((slide, index) => (
                  <button
                    key={`${slide}-reference-${index}`}
                    type="button"
                    onClick={() => {
                      detailApi?.scrollTo(index);
                      setDetailScreenshotIndex(index);
                      setFullscreenImage(slide);
                    }}
                    className={`flex w-full items-center gap-3 rounded-2xl border px-3 py-3 text-left transition ${
                      index === detailScreenshotIndex
                        ? 'border-primary bg-primary/5'
                        : 'border-border/60 bg-background hover:border-border hover:bg-muted/40'
                    }`}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border bg-muted/40 text-xs font-semibold text-muted-foreground">
                      {String(index + 1).padStart(2, '0')}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-foreground">
                        {t('templateMarketplace:details.referenceItemTitle', {
                          defaultValue: 'Reference {{index}}',
                          index: index + 1,
                        })}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t(
                          'templateMarketplace:details.referenceItemDescription',
                          {
                            defaultValue:
                              'Open this screenshot in the large preview viewer.',
                          },
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </section>
    );
  };

  if (loading) {
    return (
      <>
        <section className="relative left-1/2 right-1/2 w-screen -translate-x-1/2 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-[calc(80rem+19rem)]">
            <div className="lg:pl-[19rem]">
              <PageHeader
                title={t('templateMarketplace:title')}
                description={t('templateMarketplace:description')}
              />
            </div>
          </div>
        </section>

        <section className="relative left-1/2 right-1/2 w-screen -translate-x-1/2 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-[calc(80rem+19rem)]">
            <div className="lg:grid lg:grid-cols-[280px_minmax(0,1fr)] lg:gap-8">
              <div className="hidden lg:block">
                <Skeleton className="h-[calc(100vh-7rem)] rounded-3xl" />
              </div>
              <div>
                <Skeleton className="h-12 rounded-xl" />
                <Skeleton className="mt-6 h-72 rounded-3xl" />
                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  {[1, 2, 3, 4].map((item) => (
                    <Skeleton key={item} className="h-72 rounded-2xl" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <section className="relative left-1/2 right-1/2 w-screen -translate-x-1/2 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-[calc(80rem+19rem)]">
          <div className="lg:pl-[19rem]">
            <PageHeader
              title={t('templateMarketplace:title')}
              description={t('templateMarketplace:description')}
            />
          </div>
        </div>
      </section>

      <section className="relative left-1/2 right-1/2 w-screen -translate-x-1/2 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-[calc(80rem+19rem)]">
          <div className="lg:grid lg:grid-cols-[280px_minmax(0,1fr)] lg:gap-8">
            <aside className="hidden lg:block">
              <div className="sticky top-24 h-[calc(100vh-7rem)] overflow-y-auto pr-2">
                <div className="space-y-6 rounded-[28px] border border-border/60 bg-card/80 p-5 shadow-sm backdrop-blur">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold">
                        {t('templateMarketplace:actions.chooseTags')}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {activeFilterCount > 0
                          ? t('templateMarketplace:usageCount', {
                              count: activeFilterCount,
                            })
                          : t('templateMarketplace:filters.description')}
                      </div>
                    </div>
                    {activeFilterCount > 0 || search ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                        className="gap-1"
                      >
                        <X className="h-3.5 w-3.5" />
                        {t('templateMarketplace:actions.clearFilters')}
                      </Button>
                    ) : null}
                  </div>

                  <div className="space-y-6">
                    {categoryGroup
                      ? renderTagGroup(categoryGroup, {
                          maxVisible: 5,
                          showOverflowTrigger: true,
                        })
                      : null}
                    {secondaryGroups.map((group) =>
                      renderTagGroup(group, {
                        maxVisible: 5,
                        showOverflowTrigger: true,
                      }),
                    )}
                  </div>
                </div>
              </div>
            </aside>

            <div className="space-y-6">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('templateMarketplace:searchPlaceholder')}
                    className="pl-9"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setFilterDialogOpen(true)}
                    className="gap-2 lg:hidden"
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                    {t('templateMarketplace:actions.chooseTags')}
                    {activeFilterCount > 0 ? (
                      <Badge className="ml-1 h-5 rounded-sm px-1.5 py-0 text-[10px]">
                        {activeFilterCount}
                      </Badge>
                    ) : null}
                  </Button>
                  {activeFilterCount > 0 || search ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="gap-1 lg:hidden"
                    >
                      <X className="h-3.5 w-3.5" />
                      {t('templateMarketplace:actions.clearFilters')}
                    </Button>
                  ) : null}
                </div>
              </div>

              {selectedTagDefinitions.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedTagDefinitions.map((tag) => (
                    <div
                      key={`${tag.filterGroupId}-${tag.id}`}
                      className={getCompactTemplateBadgeClassName(
                        `inline-flex items-center border ${getTemplateTagColorClassName(tag.colorToken)} gap-1 pr-1`,
                      )}
                    >
                      <span>{tag.name}</span>
                      <button
                        type="button"
                        className="pointer-events-auto inline-flex h-4 w-4 items-center justify-center rounded-sm text-current/70 transition hover:bg-current/10 hover:text-current"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          toggleTag(tag.filterGroupId, tag.id);
                        }}
                        aria-label={t('templateMarketplace:actions.removeTag', {
                          name: tag.name,
                        })}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}

              {featuredTemplates.length > 0 ? (
                <section className="space-y-4">
                  <Carousel
                    setApi={setFeaturedApi}
                    plugins={[featuredAutoplay]}
                    opts={{ loop: true, align: 'start' }}
                    className="w-full"
                  >
                    <CarouselContent className="ml-0">
                      {featuredTemplates.map((template) => {
                        const templateTags = sortMarketplaceTags(
                          getTemplateTagDefinitions(template).filter((tag) =>
                            managedTagIds.has(tag.id),
                          ),
                        );
                        return (
                          <CarouselItem key={template.id} className="pl-0">
                            <section className="overflow-hidden rounded-[32px] border bg-card/80 shadow-sm">
                              <div className="grid min-h-[460px] gap-0 lg:grid-cols-[1.45fr_0.85fr]">
                                <div className="relative overflow-hidden bg-slate-950">
                                  <TemplatePreviewMedia
                                    template={template}
                                    enableCarousel={false}
                                  />
                                </div>
                                <div className="flex flex-col justify-between p-8 lg:p-10">
                                  <div className="space-y-5">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <Badge
                                        variant="outline"
                                        className={getCompactTemplateBadgeClassName(
                                          'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300',
                                        )}
                                      >
                                        <Star className="mr-1 h-3 w-3 fill-current" />
                                        {t(
                                          'templateMarketplace:sections.featured',
                                        )}
                                      </Badge>
                                      <span className="rounded-lg border bg-muted/60 px-2 py-1 text-xs text-muted-foreground">
                                        {t('templateMarketplace:usageCount', {
                                          count: template.usageCount || 0,
                                        })}
                                      </span>
                                    </div>
                                    <div className="space-y-3">
                                      <h2 className="text-3xl font-semibold tracking-tight lg:text-4xl">
                                        {template.name}
                                      </h2>
                                      <p className="max-w-xl text-sm leading-7 text-muted-foreground lg:text-base">
                                        {template.description}
                                      </p>
                                    </div>
                                    {templateTags.length > 0
                                      ? renderTemplateTags(template, 5)
                                      : null}
                                  </div>
                                  <div className="mt-8 flex flex-wrap gap-3">
                                    <Button
                                      variant="outline"
                                      onClick={() =>
                                        setSelectedTemplate(template)
                                      }
                                    >
                                      {t('templateMarketplace:actions.preview')}
                                    </Button>
                                    <Button
                                      onClick={() =>
                                        openCreateFlow(template.id)
                                      }
                                    >
                                      {t(
                                        'templateMarketplace:actions.useTemplate',
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </section>
                          </CarouselItem>
                        );
                      })}
                    </CarouselContent>
                    <CarouselPrevious className="left-5 border-white/20 bg-slate-950/55 text-white hover:bg-slate-950/75 hover:text-white" />
                    <CarouselNext className="right-5 border-white/20 bg-slate-950/55 text-white hover:bg-slate-950/75 hover:text-white" />
                  </Carousel>
                  <div className="flex justify-center gap-2">
                    {featuredTemplates.map((template, index) => (
                      <button
                        key={template.id}
                        type="button"
                        aria-label={template.name}
                        onClick={() => featuredApi?.scrollTo(index)}
                        className={`h-2.5 rounded-full transition ${index === featuredIndex ? 'w-10 bg-primary' : 'w-2.5 bg-muted-foreground/30 hover:bg-muted-foreground/50'}`}
                      />
                    ))}
                  </div>
                </section>
              ) : null}

              <section className="space-y-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                  <h2 className="text-lg font-semibold">
                    {t('templateMarketplace:sections.allTemplates')}
                  </h2>
                  {pagination ? (
                    <div className="text-xs text-muted-foreground">
                      {t('templateMarketplace:resultsCount', {
                        defaultValue:
                          '{{loaded}} of {{total}} templates loaded',
                        loaded: templates.length,
                        total: pagination.total,
                      })}
                    </div>
                  ) : null}
                </div>
                {templates.length === 0 ? (
                  <div className="rounded-2xl border border-dashed p-12 text-center text-muted-foreground">
                    <p className="text-base font-medium">
                      {t('templateMarketplace:empty.title')}
                    </p>
                    <p className="mt-2 text-sm">
                      {t('templateMarketplace:empty.description')}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid gap-4 xl:grid-cols-2">
                      {templates.map(renderTemplateCard)}
                    </div>
                    <div ref={loadMoreRef} className="h-1 w-full" />
                    {templatesQuery.isFetchingNextPage ? (
                      <div className="grid gap-4 xl:grid-cols-2">
                        {[1, 2].map((item) => (
                          <Skeleton
                            key={`template-page-${item}`}
                            className="h-72 rounded-2xl"
                          />
                        ))}
                      </div>
                    ) : null}
                    {!templatesQuery.hasNextPage && templates.length > 0 ? (
                      <div className="rounded-2xl border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
                        {t('templateMarketplace:endOfResults', {
                          defaultValue: 'All matching templates loaded.',
                        })}
                      </div>
                    ) : null}
                  </>
                )}
              </section>
            </div>
          </div>
        </div>
      </section>

      <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {t('templateMarketplace:filters.dialogTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('templateMarketplace:filters.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-5 overflow-y-auto pr-1">
            {orderedTagGroups.map((group) => {
              const tags = sortMarketplaceTags(group.tagDefinitions || []);
              if (!tags.length) return null;
              return (
                <div key={group.id} className="space-y-2">
                  <div>
                    <h3 className="text-sm font-medium">{group.name}</h3>
                    {group.description ? (
                      <p className="text-xs text-muted-foreground">
                        {group.description}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => {
                      const isActive = (
                        selectedTagsByGroup[group.id] || []
                      ).includes(tag.id);
                      return (
                        <Button
                          key={tag.id}
                          type="button"
                          size="sm"
                          variant={isActive ? 'default' : 'outline'}
                          onClick={() => toggleTag(group.id, tag.id)}
                          className={
                            isActive
                              ? ''
                              : getTemplateTagColorClassName(tag.colorToken)
                          }
                        >
                          {tag.name}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            {activeFilterCount > 0 ? (
              <Button
                variant="ghost"
                onClick={() => setSelectedTagsByGroup({})}
              >
                {t('templateMarketplace:actions.clearTags')}
              </Button>
            ) : null}
            <Button onClick={() => setFilterDialogOpen(false)}>
              {t('common:done', { defaultValue: 'Done' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(selectedTemplate)}
        onOpenChange={(open) => !open && setSelectedTemplate(null)}
      >
        <DialogContent className="max-w-5xl overflow-hidden p-0">
          {selectedTemplate ? (
            <div className="max-h-[85vh] overflow-y-auto">
              <div className="grid overflow-hidden lg:grid-cols-[1.2fr_0.8fr]">
                <div className="relative flex min-h-[360px] flex-col overflow-hidden bg-gradient-to-br from-background via-muted/60 to-accent/30">
                  {detailSlides.length > 0 ? (
                    <Carousel
                      setApi={setDetailApi}
                      opts={{ loop: detailSlides.length > 1 }}
                      className="flex h-full w-full flex-1 flex-col"
                    >
                      <CarouselContent className="ml-0 h-full flex-1">
                        {detailSlides.map((slide, index) => (
                          <CarouselItem
                            key={`${slide}-${index}`}
                            className="flex h-full pl-0"
                          >
                            <div className="flex h-full min-h-[360px] w-full items-center justify-center p-6 md:p-8">
                              <button
                                type="button"
                                className="flex h-full max-h-[66vh] min-h-[280px] w-full items-center justify-center overflow-hidden rounded-[28px] border border-white/30 bg-white/60 p-4 shadow-[0_24px_80px_rgba(15,23,42,0.14)] backdrop-blur dark:border-white/10 dark:bg-slate-950/20 md:p-6"
                                onClick={() => setFullscreenImage(slide)}
                              >
                                <img
                                  src={slide}
                                  alt={`${selectedTemplate.name} screenshot ${index + 1}`}
                                  loading="lazy"
                                  className="max-h-full w-auto max-w-full object-contain"
                                />
                              </button>
                            </div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      {detailSlides.length > 1 ? (
                        <>
                          <CarouselPrevious className="left-4 border-border/70 bg-card/88 text-foreground shadow-lg backdrop-blur hover:bg-card hover:text-foreground" />
                          <CarouselNext className="right-4 border-border/70 bg-card/88 text-foreground shadow-lg backdrop-blur hover:bg-card hover:text-foreground" />
                          <div className="flex items-center justify-center gap-1.5 border-t border-border/40 bg-background/30 px-6 py-4">
                            {detailSlides.map((slide, index) => (
                              <button
                                key={`${slide}-dot-${index}`}
                                type="button"
                                aria-label={`slide-${index + 1}`}
                                onClick={() => detailApi?.scrollTo(index)}
                                className={`h-2.5 rounded-full transition ${index === detailScreenshotIndex ? 'w-8 bg-foreground' : 'w-2.5 bg-foreground/25 hover:bg-foreground/45'}`}
                              />
                            ))}
                          </div>
                        </>
                      ) : null}
                    </Carousel>
                  ) : (
                    <TemplatePreviewMedia template={selectedTemplate} />
                  )}
                </div>

                <div className="flex flex-col overflow-hidden">
                  <DialogHeader className="border-b px-6 py-5 text-left">
                    <div className="flex flex-wrap items-center gap-2">
                      {selectedTemplate.featured ? (
                        <Badge
                          variant="outline"
                          className={getCompactTemplateBadgeClassName(
                            'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300',
                          )}
                        >
                          <Star className="mr-1 h-3 w-3 fill-current" />
                          {t('templateMarketplace:featured')}
                        </Badge>
                      ) : null}
                      <div className="inline-flex items-center gap-1 rounded-lg border bg-muted/40 px-2 py-1 text-xs text-muted-foreground">
                        <BarChart3 className="h-3.5 w-3.5" />
                        {t('templateMarketplace:usageCount', {
                          count: selectedTemplate.usageCount || 0,
                        })}
                      </div>
                    </div>
                    <DialogTitle className="pt-3 text-2xl">
                      {selectedTemplate.name}
                    </DialogTitle>
                    <DialogDescription className="pt-1 text-sm leading-6 text-muted-foreground">
                      {selectedTemplate.description}
                    </DialogDescription>
                  </DialogHeader>

                  <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
                    <div className="space-y-2">
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                        {t('templateMarketplace:details.tags')}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {sortMarketplaceTags(
                          getTemplateTagDefinitions(selectedTemplate).filter(
                            (tag) => managedTagIds.has(tag.id),
                          ),
                        ).map((tag) => (
                          <Badge
                            key={`${selectedTemplate.id}-detail-${tag.id}`}
                            variant="outline"
                            className={getCompactTemplateBadgeClassName(
                              getTemplateTagColorClassName(tag.colorToken),
                            )}
                          >
                            {tag.name}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {detailSlides.length > 0 ? (
                      <div className="space-y-2">
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                          {t('templateMarketplace:details.screenshots')}
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          {detailSlides.map((slide, index) => (
                            <button
                              key={`${slide}-thumb-${index}`}
                              type="button"
                              onClick={() => detailApi?.scrollTo(index)}
                              className={`overflow-hidden rounded-xl border transition ${index === detailScreenshotIndex ? 'border-primary shadow-sm' : 'border-border/60 hover:border-border'}`}
                            >
                              <img
                                src={slide}
                                alt={`${selectedTemplate.name} thumbnail ${index + 1}`}
                                loading="lazy"
                                className="aspect-[16/10] h-full w-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <DialogFooter className="border-t px-6 py-4">
                    <Button
                      variant="outline"
                      onClick={() => setSelectedTemplate(null)}
                    >
                      {t('common:close', { defaultValue: 'Close' })}
                    </Button>
                    <Button onClick={() => openCreateFlow(selectedTemplate.id)}>
                      {t('templateMarketplace:actions.useTemplate')}
                    </Button>
                  </DialogFooter>
                </div>
              </div>

              {renderDetailContent(selectedTemplate)}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(fullscreenImage)}
        onOpenChange={(open) => !open && setFullscreenImage(null)}
      >
        <DialogContent className="h-[80vh] w-[80vw] max-w-[80vw] border-border/60 bg-background/96 p-3 shadow-2xl backdrop-blur">
          {fullscreenImage ? (
            <div
              ref={fullscreenViewerRef}
              role="dialog"
              tabIndex={0}
              className={`flex h-full w-full items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-background via-muted/60 to-accent/30 ${fullscreenZoom > 1 ? 'cursor-grab' : ''}`}
              onWheel={(event) => {
                hideFullscreenHint();
                if (event.ctrlKey || event.metaKey) {
                  event.preventDefault();
                  setFullscreenZoom((current) => {
                    const nextZoom = clampFullscreenZoom(
                      current + (event.deltaY < 0 ? 0.12 : -0.12),
                    );
                    if (nextZoom <= 1) {
                      setFullscreenPan({ x: 0, y: 0 });
                    }
                    return nextZoom;
                  });
                  return;
                }

                if (fullscreenZoom > 1) {
                  event.preventDefault();
                  setFullscreenPan((current) => ({
                    x: current.x - event.deltaX,
                    y: current.y - event.deltaY,
                  }));
                  return;
                }

                const now = Date.now();
                if (now - fullscreenWheelAtRef.current < 220) return;
                fullscreenWheelAtRef.current = now;
                stepFullscreenSlide(event.deltaY > 0 ? 1 : -1);
              }}
              onMouseDown={(event) => {
                if (fullscreenZoom <= 1) return;
                hideFullscreenHint();
                fullscreenDragRef.current = {
                  active: true,
                  originX: fullscreenPan.x,
                  originY: fullscreenPan.y,
                  startX: event.clientX,
                  startY: event.clientY,
                };
              }}
              onMouseMove={(event) => {
                if (!fullscreenDragRef.current.active || fullscreenZoom <= 1)
                  return;
                hideFullscreenHint();
                const deltaX = event.clientX - fullscreenDragRef.current.startX;
                const deltaY = event.clientY - fullscreenDragRef.current.startY;
                setFullscreenPan({
                  x: fullscreenDragRef.current.originX + deltaX,
                  y: fullscreenDragRef.current.originY + deltaY,
                });
              }}
              onMouseUp={() => {
                fullscreenDragRef.current.active = false;
              }}
              onMouseLeave={() => {
                fullscreenDragRef.current.active = false;
              }}
            >
              <button
                type="button"
                className={`flex h-full w-full items-center justify-center p-6 ${fullscreenZoom > 1 ? 'cursor-grabbing' : ''}`}
                onClick={() => {
                  hideFullscreenHint();
                  if (
                    !fullscreenDragRef.current.active &&
                    fullscreenZoom <= 1.02
                  ) {
                    setFullscreenImage(null);
                  }
                  fullscreenDragRef.current.active = false;
                }}
                title="Scroll to change screenshot, Ctrl/Cmd + scroll to zoom, click to close"
              >
                <img
                  src={fullscreenImage}
                  alt="Template screenshot full size"
                  className="max-h-full max-w-full object-contain transition-transform duration-150 ease-out select-none"
                  draggable={false}
                  style={{
                    transform: `translate(${fullscreenPan.x}px, ${fullscreenPan.y}px) scale(${fullscreenZoom})`,
                    transformOrigin: 'center center',
                  }}
                />
              </button>
              <div
                className={`pointer-events-none absolute bottom-5 left-1/2 -translate-x-1/2 transition duration-300 ${showFullscreenHint ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`}
              >
                <div className="rounded-full border border-white/20 bg-slate-950/72 px-4 py-2 text-xs text-white shadow-lg backdrop-blur">
                  <span className="font-medium">Scroll</span> switch images
                  <span className="mx-2 text-white/45">•</span>
                  <span className="font-medium">Ctrl/Cmd + Scroll</span> zoom
                  <span className="mx-2 text-white/45">•</span>
                  <span className="font-medium">Drag</span> pan when zoomed
                  <span className="mx-2 text-white/45">•</span>
                  <span className="font-medium">← → +/- 0</span> keyboard
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
